import type { App } from 'vue';
import type { Router } from 'vue-router';
import * as Sentry from '@sentry/vue';

/** 初始化 Sentry */
export function initSentry(app: App, router: Router) {
  try {
    // 确保环境变量正确获取（Vue CLI 会自动注入以 VUE_APP_ 开头的环境变量）
    // CI_COMMIT_TAG 通过 webpack DefinePlugin 在构建时注入
    const release = process.env.CI_COMMIT_TAG || process.env.VUE_APP_RELEASE || undefined;
    const environment = process.env.VUE_APP_ENV || process.env.NODE_ENV || 'development';

    Sentry.init({
      app,
      dsn: 'https://bd20f46888c54db9840e5155154edcb3@alisgp-sentry.d.xiaomi.net/47',
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router)
        })
      ],

      release,
      environment,

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // 根据实际后端 API 地址配置，这里需要替换为实际的 API 域名
      tracePropagationTargets: ['localhost', /^https?:\/\/.*\.mi\.com/],
      // Session tracking 配置 - 必须启用才能看到 Crash Free Sessions/Users
      // autoSessionTracking: true 会自动追踪所有会话，无需额外配置采样率
      autoSessionTracking: true,
      // 错误采样率 - 1.0 表示 100% 的错误会被捕获
      sampleRate: 1.0,

      // 添加调试选项 - 设为 false 可关闭 Sentry 内部日志（如 [Tracing] Starting 'measure' span）
      debug: false,

      // 配置 beforeSend 钩子来过滤或修改事件
      beforeSend(event, hint) {
        // 开发环境下打印事件信息，方便调试
        // if (environment === 'development') {
        //   console.log('[Sentry] Event captured:', event);
        // }
        return event;
      }
    });

    // 开发环境下输出初始化信息
    if (environment === 'development') {
      console.log('[Sentry] Initialized successfully', {
        dsn: 'https://bd20f46888c54db9840e5155154edcb3@alisgp-sentry.d.xiaomi.net/47',
        environment,
        release,
        sessionTracking: true
      });

      // 测试错误捕获（仅开发环境）
      // Sentry.captureMessage('Sentry 初始化测试消息', 'info');
    }
  } catch (error) {
    console.error('[Sentry] 初始化失败:', error);
  }
}

/**
 * 设置 Sentry 用户信息
 * 必须在用户信息获取后调用，才能正确追踪 Crash Free Users
 */
export function setSentryUser(userInfo: Project.IUserInfo) {
  try {
    Sentry.setUser({
      id: userInfo.account || userInfo.miID || undefined,
      username: userInfo.userName || userInfo.account || undefined,
      email: userInfo.email || undefined
      // Sentry 会自动获取 IP 地址，无需手动设置
    });

    if (process.env.VUE_APP_ENV === 'development') {
      console.log('[Sentry] User set:', {
        id: userInfo.account || userInfo.miID,
        username: userInfo.userName || userInfo.account,
        email: userInfo.email
      });
    }
  } catch (error) {
    console.error('[Sentry] 设置用户信息失败:', error);
  }
}
